import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../ui/badge';
import { ShieldCheck, FileBadge } from 'lucide-react';

const sevBorder = {
  critical: 'border-l-red-500',
  urgent: 'border-l-amber-500',
  normal: 'border-l-[hsl(var(--brand-accent))]',
};

const daysLabel = (d) => {
  if (d === null || d === undefined) return '';
  if (d < 0) return `Expired ${Math.abs(d)} day${d === -1 ? '' : 's'} ago`;
  if (d === 0) return 'Expires today';
  return `${d} day${d === 1 ? '' : 's'} left`;
};

export const CompanyLicenseRenewalsWidget = ({ data }) => {
  const navigate = useNavigate();
  const items = data?.items || [];

  if (!items.length) {
    return (
      <div className="flex items-center gap-3 py-3" data-testid="company-license-renewals-empty">
        <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
        <div>
          <p className="text-sm font-medium text-fg">No licenses need attention.</p>
          <p className="text-xs text-fg-muted mt-0.5">
            No company licenses are expiring in the next 90 days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="company-license-renewals-list">
      <div className="flex items-center gap-2 flex-wrap">
        {data.expired > 0 && (
          <Badge variant="destructive" className="font-semibold" data-testid="company-license-expired-count">
            {data.expired} expired
          </Badge>
        )}
        {data.soon > 0 && (
          <Badge variant="outline" className="font-semibold" data-testid="company-license-soon-count">
            {data.soon} renewing soon
          </Badge>
        )}
      </div>

      <ul className="space-y-1.5 max-h-[26rem] overflow-y-auto pr-1" data-testid="company-license-renewals-scroll">
        {items.map((item, i) => (
          <li key={item.id || i}>
            <button
              type="button"
              onClick={() => navigate('/compliance/licenses')}
              className={`w-full text-left flex items-start gap-3 p-2.5 rounded-md border-l-2
                ${sevBorder[item.severity] || sevBorder.normal}
                bg-surface-sunken hover:bg-[hsl(var(--hover-overlay))]
                transition-colors duration-150 focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-accent))]`}
              data-testid={`company-license-item-${i}`}
            >
              <FileBadge className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-fg truncate">{item.license_type}</p>
                <p className="text-xs text-fg-muted truncate">
                  {item.location}{item.license_number ? ` · #${item.license_number}` : ''}
                </p>
              </div>
              <span className={`text-xs whitespace-nowrap shrink-0 pt-0.5 ${item.days_until < 0 ? 'text-red-600' : item.days_until <= 30 ? 'text-amber-600' : 'text-fg-muted'}`}>
                {daysLabel(item.days_until)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CompanyLicenseRenewalsWidget;
